import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";

import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import { SearchableSelect } from "../../components/SearchableSelect";
import {
  getSupportSubject,
  getSupportSubjectPath,
  supportSubjects,
} from "../../constants/supportSubjects";
import { useRequireAuth } from "../../constants/urlsClientSide";
import {
  fetchSupportJson,
  getSupportCasePath,
  getSupportSeverityStringKey,
  supportPagePath,
  SupportRequestError,
} from "../../helpers/supportCases";
import { PartnerAccessLevel, usePartnerRouteGuard } from "../../helpers/usePartnerRouteGuard";
import { useStrings } from "../../localization/localizationHelpers";
import {
  CreateSupportCaseRequest,
  supportCaseSeverities,
  SupportCaseSeverity,
  SupportGame,
} from "../../models/supportCases";
import * as styles from "./PartnerSupportNewPage.module.css";

export type SupportNewCaseLocationState = Readonly<{ attachmentFailed?: boolean }>;

type Option<T extends string> = Readonly<{ value: T; label: string; description: string }>;

const subjectKeys = supportSubjects.map(subject => subject.stringKey);
const severityKeys = supportCaseSeverities.map(getSupportSeverityStringKey);
const severityDescriptionKeys = severityKeys.map(key => `${key}Description`);

export const PartnerSupportNewPage: React.FC = () => {
  const isPageReady = usePartnerRouteGuard(PartnerAccessLevel.PartnerSettings);
  const isSignedIn = useRequireAuth();
  const history = useHistory();
  const prefill = new URLSearchParams(useLocation().search);
  const [games, setGames] = useState<ReadonlyArray<SupportGame>>([]);
  const [title, setTitle] = useState(() => (prefill.get("title") ?? "").slice(0, 200));
  const [subjectId, setSubjectId] = useState(
    () => getSupportSubject(prefill.get("subjectId") ?? undefined)?.id ?? ""
  );
  const [severity, setSeverity] = useState<SupportCaseSeverity | "">(
    () => supportCaseSeverities.find(value => value === prefill.get("severity")) ?? ""
  );
  const [isGameRelated, setIsGameRelated] = useState(() => !!prefill.get("gameConceptId"));
  const [gameConceptId, setGameConceptId] = useState(() => prefill.get("gameConceptId") ?? "");
  const [description, setDescription] = useState(() =>
    (prefill.get("description") ?? "").slice(0, 2000)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [
    supportLabel,
    pageTitle,
    cancel,
    submit,
    submitting,
    titleLabel,
    categoryLabel,
    selectCategory,
    severityLabel,
    selectSeverity,
    gameQuestion,
    yes,
    no,
    gameLabel,
    selectGame,
    describeIssue,
    describePlaceholder,
    required,
    noResults,
    accessDenied,
    submitErrorText,
  ] = useStrings(
    "hubSupportRequestsTitle",
    "hubSupportSubmitRequest",
    "cancel",
    "ticketSubmitLabel",
    "ticketSubmittingLabel",
    "ticketTitleLabel",
    "ticketCategoryLabel",
    "ticketSelectCategory",
    "ticketSeverityLabel",
    "ticketSelectSeverity",
    "hubSupportGameRelatedQuestion",
    "yes",
    "no",
    "hubSupportGameLabel",
    "hubSupportSelectGame",
    "hubSupportDescribeIssue",
    "hubSupportDescribePlaceholder",
    "required",
    "noResultsFound",
    "ticketAccessDeniedMsg",
    "hubSupportSubmitError"
  );
  const subjectLabels = useStrings(...subjectKeys);
  const severityLabels = useStrings(...severityKeys);
  const severityDescriptions = useStrings(...severityDescriptionKeys);

  const subjectOptions = supportSubjects.map(subject => {
    const path = getSupportSubjectPath(subject.id).map(
      item => subjectLabels[subjectKeys.indexOf(item.stringKey)]
    );
    return {
      value: subject.id,
      label: path[path.length - 1],
      path: path.join(" › "),
      depth: path.length - 1,
    };
  });
  const severityOptions: ReadonlyArray<Option<SupportCaseSeverity>> = supportCaseSeverities.map(
    (value, index) => ({
      value,
      label: severityLabels[index],
      description: severityDescriptions[index],
    })
  );

  useEffect(() => {
    if (!isPageReady || !isSignedIn) {
      return;
    }
    let isCurrent = true;
    fetchSupportJson<SupportGame[]>("/games")
      .then(result => isCurrent && setGames(result))
      .catch(() => isCurrent && setGames([]));
    return () => {
      isCurrent = false;
    };
  }, [isPageReady, isSignedIn]);

  const selectedGameId =
    games.find(game => game.gameConceptId.toLowerCase() === gameConceptId.toLowerCase())
      ?.gameConceptId ?? "";
  const isValid =
    !!title.trim() &&
    !!subjectId &&
    !!severity &&
    !!description.trim() &&
    (!isGameRelated || !!selectedGameId);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid || isSubmitting || !severity) {
      return;
    }
    setIsSubmitting(true);
    setSubmitError("");
    const request: CreateSupportCaseRequest = {
      title: title.trim(),
      subjectId,
      severity,
      description: description.trim(),
      gameConceptId: isGameRelated ? selectedGameId : undefined,
    };
    try {
      const { caseId } = await fetchSupportJson<{ caseId: string }>("/cases", {
        method: "POST",
        body: JSON.stringify(request),
      });
      history.push(getSupportCasePath(caseId));
    } catch (error: unknown) {
      setSubmitError(
        error instanceof SupportRequestError && error.status === 403
          ? accessDenied
          : submitErrorText
      );
      setIsSubmitting(false);
    }
  };

  const renderSelect = <T extends string>(
    id: string,
    label: string,
    placeholder: string,
    value: T | "",
    onChange: (value: T) => void,
    options: ReadonlyArray<Option<T>>
  ) => {
    const selected = options.find(option => option.value === value);
    return (
      <div className={styles.field} key={id}>
        <label htmlFor={id}>{label}</label>
        <select
          id={id}
          value={value}
          required
          aria-describedby={selected?.description ? `${id}-hint` : undefined}
          onChange={event => onChange(event.target.value as T)}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {selected?.description && (
          <span id={`${id}-hint`} className={styles.hint}>
            {selected.description}
          </span>
        )}
      </div>
    );
  };

  const breadcrumbSteps = [{ label: supportLabel, to: supportPagePath }];

  if (!isPageReady || !isSignedIn) {
    return (
      <PartnerPageLayout breadcrumbLabel={pageTitle} breadcrumbSteps={breadcrumbSteps}>
        <PageLoadingSpinner />
      </PartnerPageLayout>
    );
  }

  return (
    <PartnerPageLayout breadcrumbLabel={pageTitle} breadcrumbSteps={breadcrumbSteps}>
      <form onSubmit={onSubmit} noValidate data-testid="support-new-form">
        <header className={styles.header}>
          <h1>{title.trim() || pageTitle}</h1>
          <div className={styles.actions}>
            <Link to={supportPagePath} className={styles.secondaryButton}>
              {cancel}
            </Link>
            <button
              type="submit"
              className={styles.primaryButton}
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? submitting : submit}
            </button>
          </div>
        </header>
        {submitError && (
          <p role="alert" className={styles.error}>
            {submitError}
          </p>
        )}
        <section className={styles.card}>
          <div className={styles.field}>
            <label htmlFor="support-title">{titleLabel}</label>
            <input
              id="support-title"
              value={title}
              maxLength={200}
              required
              onChange={event => setTitle(event.target.value)}
            />
          </div>
          <SearchableSelect
            id="support-subject"
            className={styles.field}
            label={categoryLabel}
            placeholder={selectCategory}
            noResultsLabel={noResults}
            value={subjectId}
            options={subjectOptions}
            onChange={setSubjectId}
            required
          />
          <fieldset className={styles.field}>
            <legend>{gameQuestion}</legend>
            <div className={styles.radios}>
              <label>
                <input
                  type="radio"
                  name="support-game-related"
                  checked={isGameRelated}
                  onChange={() => setIsGameRelated(true)}
                />
                {yes}
              </label>
              <label>
                <input
                  type="radio"
                  name="support-game-related"
                  checked={!isGameRelated}
                  onChange={() => setIsGameRelated(false)}
                />
                {no}
              </label>
            </div>
          </fieldset>
          {isGameRelated &&
            renderSelect(
              "support-game",
              gameLabel,
              selectGame,
              selectedGameId,
              setGameConceptId,
              games.map(game => ({
                value: game.gameConceptId,
                label: `${game.name} · ${game.gameConceptId}`,
                description: "",
              }))
            )}
        </section>
        <section className={styles.card}>
          <div className={styles.field}>
            <label htmlFor="support-description">
              {describeIssue} <span className={styles.badge}>{required}</span>
            </label>
            <textarea
              id="support-description"
              rows={6}
              value={description}
              placeholder={describePlaceholder}
              required
              onChange={event => setDescription(event.target.value)}
            />
          </div>
          {renderSelect(
            "support-severity",
            severityLabel,
            selectSeverity,
            severity,
            setSeverity,
            severityOptions
          )}
        </section>
      </form>
    </PartnerPageLayout>
  );
};
